import { State } from 'model/State';
import { CustomerAccountOverviewSearchDTO } from '../models';
import { selectSwitchedUser } from '../selectors';
import {
  getFromHamsClientNonExternalAccountAPI,
  getFromHamsClientPublicAPI,
} from 'util/mac/api/account/get';
import { fetchWithParamValidation } from 'util/fetchWithParamValidation';
import {
  postFromHamsClientPublicAPI,
  postFromHamsNonExternalClientPublicAPI,
  postToHamsClient,
} from 'util/mac/api/account/post';

export const detail = async (accountId: string, state: State, ufoName: string) =>
  fetchWithParamValidation(
    ({ id }) => getFromHamsClientPublicAPI(`/${id}`, state, ufoName, { periods: false }),
    {
      id: accountId,
    },
  );

export const getCustomerAccountOverviewsByPartnerSearchDTO = async (
  searchObject: CustomerAccountOverviewSearchDTO,
  state: State,
  max: number,
  offset: number,
  total: boolean,
) =>
  fetchWithParamValidation(
    ({ maxValue, offsetValue, totalValue }) => {
      return postFromHamsClientPublicAPI(
        '/getOverviews',
        state,
        {
          max: maxValue,
          offset: offsetValue,
          total: totalValue,
        },
        searchObject,
      );
    },
    {
      maxValue: max,
      offsetValue: offset,
      totalValue: total,
    },
  );

export const isUpgradableAccount = async (accountId: number, state: State, ufoName: string) => {
  const email = selectSwitchedUser(state);
  return fetchWithParamValidation(
    ({ accountIdValue, emailValue }) =>
      postFromHamsClientPublicAPI(
        '/isUpgradableAccount',
        state,
        undefined,
        {
          accountId: accountIdValue,
          email: emailValue,
        },
        ufoName,
      ),
    {
      accountIdValue: accountId,
      emailValue: email,
    },
  );
};

export const isRenewableAccount = async (accountId: number, state: State, ufoName: string) => {
  const email = selectSwitchedUser(state);
  return fetchWithParamValidation(
    ({ accountIdValue, emailValue }) =>
      postFromHamsClientPublicAPI(
        '/isRenewableAccount',
        state,
        undefined,
        {
          accountId: accountIdValue,
          email: emailValue,
        },
        ufoName,
      ),
    {
      accountIdValue: accountId,
      emailValue: email,
    },
  );
};

export const isEvaluationAllowed = async (username: string, productKey: string, state: State) =>
  fetchWithParamValidation(
    ({ usernameValue, productKeyValue }) =>
      postFromHamsClientPublicAPI('/evaluationAllowed', state, undefined, {
        username: usernameValue,
        productKey: productKeyValue,
      }),
    {
      usernameValue: username,
      productKeyValue: productKey,
    },
  );

export const getAsyncCustomerAccountOverviewsByPartnerSearchDTO = async (
  searchDTO: CustomerAccountOverviewSearchDTO,
  state: State,
) =>
  fetchWithParamValidation(
    () => postFromHamsClientPublicAPI('/getOverviewsAsync', state, undefined, searchDTO),
    {},
  );

export const validate = (
  accountId: string,
  contactId: string,
  contactRelationshipType: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, currentContactId, relationshipType }) =>
      postToHamsClient(
        `/${id}/validateCurrencyWithContactAsMain/${currentContactId}/${relationshipType
          .toString()
          .toUpperCase()}`,
        state,
      ),
    {
      id: accountId,
      currentContactId: contactId,
      relationshipType: contactRelationshipType,
    },
  );

export const getAppParentSen = async (appAccountId: string, state: State) =>
  fetchWithParamValidation(
    ({ id }) => getFromHamsClientNonExternalAccountAPI(`/dcAppParentMapping/${id}`, state),
    {
      id: appAccountId,
    },
  );

export const postAppParentSen = async (
  appAccountId: string,
  parentAccountId: string,
  parentAccountSlug: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ appAccountIdValue, parentAccountIdValue, parentAccountSlugValue }) =>
      postFromHamsNonExternalClientPublicAPI(`/dcAppParentMapping`, state, undefined, {
        appAccountId: appAccountIdValue,
        parentAccountId: parentAccountIdValue,
        parentAccountSlug: parentAccountSlugValue,
        source: 'MAC',
      }),
    {
      appAccountIdValue: appAccountId,
      parentAccountIdValue: parentAccountId,
      parentAccountSlugValue: parentAccountSlug,
    },
  );

export const getAppParentBySen = async (parentId: string, state: State) =>
  fetchWithParamValidation(
    ({ id }) =>
      getFromHamsClientNonExternalAccountAPI(`/dcAppParentMapping/parent/id/${id}`, state),
    {
      id: parentId,
    },
  );

export const getAppParentBySlug = async (parentId: string, state: State) =>
  fetchWithParamValidation(
    ({ id }) =>
      getFromHamsClientNonExternalAccountAPI(`/dcAppParentMapping/parent/slug/${id}`, state),
    {
      id: parentId,
    },
  );
